/**
 * Support (docs/specs/support.md): the built-in Support bot every workspace has, the feedback form, its limits and
 * dedupe, the Help menu links, and how the Bundle team's request board moves. Pure; the API, web and native apps
 * share it (vectors in packages/test-vectors/logic/support).
 */
import type { FeedbackKind, Member, SupportDiagnostics } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";

/** The Support bot's member id in every workspace, its app id (AppAuthor / agent.appId) and handle. */
export const SUPPORT_USER_ID = "bundle-support";
export const SUPPORT_APP_ID = "support";
export const SUPPORT_HANDLE = "support";

/** Its name is translated on the client, in the viewer's language; this is only the stored fallback. */
export const SUPPORT_STORED_NAME = "Support";

export const FEEDBACK_KINDS: readonly FeedbackKind[] = ["bug", "idea", "question"];
export const FEEDBACK_MIN = 10;
export const FEEDBACK_MAX = 4000;
/** The counter shows from here on. */
export const FEEDBACK_COUNTER_FROM = 3500;
export const SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

export type RateLimit = { max: number; windowMs: number };
export const FORM_LIMITS: readonly RateLimit[] = [
  { max: 5, windowMs: HOUR },
  { max: 20, windowMs: DAY },
];
export const AGENT_PERSON_LIMITS: readonly RateLimit[] = [{ max: 30, windowMs: HOUR }];
export const AGENT_WORKSPACE_LIMITS: readonly RateLimit[] = [{ max: 300, windowMs: DAY }];
/** Same person, same fingerprint, within this: no new card. */
export const SELF_DUPLICATE_MS = DAY;
/** Someone else's open card with the same fingerprint, within this: a vote. */
export const VOTE_DUPLICATE_MS = 7 * DAY;

/** Whether a member is the Support bot (by id, or by app id for safety). */
export function isSupportMember(m: { userId: string; agent?: { appId?: string | undefined } | undefined }): boolean {
  return m.userId === SUPPORT_USER_ID || m.agent?.appId === SUPPORT_APP_ID;
}

/** The bot's name in the viewer's language. */
export function botName(): MessageDescriptor {
  return msg("support.botName");
}

/** A member's name as shown: Support's in the viewer's language (a descriptor), anyone else's display name. */
export function memberLabel(m: Member): MessageDescriptor | string {
  return isSupportMember(m) ? botName() : m.displayName;
}

/**
 * The members with Support's names (display and full) in the viewer's language, so every place that shows a name
 * (sidebar, message authors, headers, mention chips, autocomplete, notifications) shows it translated. Returns the
 * same record when there is nothing to change. `translatedName` = the platform's resolution of `botName()`.
 */
export function localizeMembers(members: Record<string, Member>, translatedName: string): Record<string, Member> {
  let out: Record<string, Member> | undefined;
  for (const id of Object.keys(members)) {
    const m = members[id]!;
    if (!isSupportMember(m) || (m.displayName === translatedName && m.fullName === translatedName)) continue;
    out ??= { ...members };
    out[id] = { ...m, displayName: translatedName, fullName: translatedName };
  }
  return out ?? members;
}

/** Whether a mention query matches Support: the handle or the translated name, by prefix, ignoring case and accents. */
export function matchesMention(query: string, translatedName: string): boolean {
  const q = fold(query).replace(/^@/, "");
  if (q === "") return true;
  return [SUPPORT_HANDLE, SUPPORT_STORED_NAME, translatedName].some((n) => fold(n).startsWith(q));
}

function fold(s: string): string {
  return s.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
}

export type FeedbackCheck = { ok: true } | { ok: false; error: MessageDescriptor };

/** The form's text: 10–4000 characters after trimming. */
export function checkText(text: string): FeedbackCheck {
  const n = text.trim().length;
  if (n < FEEDBACK_MIN) return { ok: false, error: msg("support.form.tooShort", { min: FEEDBACK_MIN }) };
  if (n > FEEDBACK_MAX) return { ok: false, error: msg("support.form.tooLong", { max: FEEDBACK_MAX }) };
  return { ok: true };
}

/** A screenshot: one image, at most 5 MB. */
export function checkScreenshot(file: { mime: string; size: number }): FeedbackCheck {
  if (!file.mime.startsWith("image/")) return { ok: false, error: msg("support.form.notImage") };
  if (file.size > SCREENSHOT_MAX_BYTES) return { ok: false, error: msg("support.form.tooBig", { mb: 5 }) };
  return { ok: true };
}

/** "3612 / 4000" from 3500 characters on, else null. */
export function counter(text: string): string | null {
  const n = text.trim().length;
  return n >= FEEDBACK_COUNTER_FROM ? `${n} / ${FEEDBACK_MAX}` : null;
}

/** The text field's label and placeholder for a kind. */
export function kindCopy(kind: FeedbackKind): { name: MessageDescriptor; label: MessageDescriptor; placeholder: MessageDescriptor } {
  switch (kind) {
    case "bug":
      return { name: msg("support.kind.bug"), label: msg("support.form.bug.label"), placeholder: msg("support.form.bug.placeholder") };
    case "idea":
      return { name: msg("support.kind.idea"), label: msg("support.form.idea.label"), placeholder: msg("support.form.idea.placeholder") };
    case "question":
      return { name: msg("support.kind.question"), label: msg("support.form.question.label"), placeholder: msg("support.form.question.placeholder") };
  }
}

/**
 * Dedupe key: kind + the text lower-cased, accents stripped, URLs and digits removed, punctuation and whitespace
 * folded, first 400 characters, hashed with FNV-1a (32-bit, hex).
 */
export function fingerprint(kind: FeedbackKind, text: string): string {
  const folded = fold(text)
    .replace(/https?:\/\/\S+/g, " ")
    .replace(/[0-9]+/g, " ")
    .replace(/[^\p{L}]+/gu, " ")
    .trim()
    .slice(0, 400);
  return fnv1a(`${kind}:${folded}`);
}

function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

export type RateCheck = { ok: true } | { ok: false; retryAt: number };

/**
 * Sliding-window limits over past event times (any order). Not ok when any window already holds `max` events;
 * `retryAt` is when the oldest event in the fullest window leaves it (the latest such time across windows).
 */
export function rateCheck(times: readonly number[], now: number, limits: readonly RateLimit[]): RateCheck {
  let retryAt = 0;
  for (const l of limits) {
    const inWindow = times.filter((t) => t > now - l.windowMs && t <= now).sort((a, b) => a - b);
    if (inWindow.length >= l.max) retryAt = Math.max(retryAt, inWindow[inWindow.length - l.max]! + l.windowMs);
  }
  return retryAt === 0 ? { ok: true } : { ok: false, retryAt };
}

/** The longest window, for pruning stored event times. */
export function longestWindow(limits: readonly RateLimit[]): number {
  return Math.max(...limits.map((l) => l.windowMs));
}

/** A reply in #support-bundle that starts with // stays with the team. */
export function isInternalNote(text: string): boolean {
  return text.trimStart().startsWith("//");
}

/** How a team reply is signed in the customer's DM: first name · Bundle. */
export function teamAuthorName(fullName: string): string {
  const first = fullName.trim().split(/\s+/)[0] ?? "";
  return first === "" ? "Bundle" : `${first} · Bundle`;
}

/** The team thread's title: "Acme · Ana Ribeiro". */
export function threadTitle(workspaceName: string, personName: string): string {
  return `${workspaceName} · ${personName}`;
}

/** Board status keys, in column order. */
export const STATUSES = ["answered", "human", "progress", "done"] as const;
export type RequestStatus = (typeof STATUSES)[number];
export type RequestEvent = "agentAnswered" | "handoff" | "teamReply" | "customerReply";

/**
 * How a card moves. The agent answering only marks a fresh card answered; a hand-off asks for a person unless the
 * team already has it; a team reply means in progress; a customer writing on a done card reopens it for a person.
 */
export function nextStatus(current: RequestStatus | undefined, event: RequestEvent): RequestStatus | undefined {
  switch (event) {
    case "agentAnswered":
      return current === undefined ? "answered" : current;
    case "handoff":
      return current === "progress" || current === "done" ? current : "human";
    case "teamReply":
      return current === "done" ? "done" : "progress";
    case "customerReply":
      return current === "done" ? "human" : current;
  }
}

const SITE = "https://bundle.direct";

function sitePath(locale: string, path: string): string {
  return locale.toLowerCase().startsWith("pt") ? `${SITE}/pt-br${path}` : `${SITE}${path}`;
}

/** Help center (the site's support page) in the viewer's language. */
export function helpCenterUrl(locale: string): string {
  return sitePath(locale, "/support");
}

/** What's new (the blog) in the viewer's language. */
export function whatsNewUrl(locale: string): string {
  return sitePath(locale, "/blog");
}

/** The rows under "What's included", in order; absent fields are left out. */
export function diagnosticsRows(d: SupportDiagnostics): { label: MessageDescriptor; value: string }[] {
  const rows: { label: MessageDescriptor; value: string }[] = [];
  const app = [d.appVersion, d.platform, d.browser, d.os].filter((v): v is string => v !== undefined && v !== "").join(" · ");
  rows.push({ label: msg("support.diag.app"), value: app });
  rows.push({ label: msg("support.diag.language"), value: [d.locale, d.timeZone].filter((v): v is string => v !== undefined && v !== "").join(" · ") });
  if (d.screen !== undefined) rows.push({ label: msg("support.diag.screen"), value: d.screen });
  if (d.connection !== undefined) rows.push({ label: msg("support.diag.connection"), value: d.connection });
  return rows;
}
